'use client'
import type { FC, ReactElement } from 'react'
import { useMemo } from 'react'
import type { FlexProps } from '@chakra-ui/react'
import { Box, Flex, HStack, StackDivider } from '@chakra-ui/react'
import { usePathname } from 'next/navigation'
import HoverableComponent from '../../common/HoverableComponent'
import GrandIconButton from '../../common/GrandIconButton'
import GrandNextLink from '../../common/GrandNextLink'
import { useUIGlobalsContext } from '../../lib/ui-globals/hooks'
import type { MenuItem } from './MenuItem'
import ContentContainer from './ContentContainer'
import { TopNavContentMenuItem } from './TopNavContentMenuItem'

export interface NavBarAction {
  id: number
  component: ReactElement
  showsOnMobile: boolean
}

export interface TopNavContentProps extends FlexProps {
  menuItems: { categories: MenuItem[]; other: MenuItem[] }
  navbarActions: NavBarAction[]
  logo: ReactElement
  onMenuHover: (isInside: boolean) => void
  onMenuToggle: (menuItems: MenuItem[]) => void
  toggleIcon: ReactElement
  // Dropdown category menu is hidden
  isHidden: boolean
}

const TopNavContent: FC<TopNavContentProps> = ({
  onMenuHover,
  onMenuToggle,
  toggleIcon,
  menuItems,
  navbarActions,
  logo,
  isHidden,
  ...props
}) => {
  const pathname = usePathname()
  const { menuMaxContentWidth } = useUIGlobalsContext()

  const menuItemsFull = useMemo(() => {
    const mi = [...menuItems.categories, ...menuItems.other]
    // removes non-serializable values so we can pass then through extraArgs

    return mi.map(({ onClick, onHoverLeave, onHoverEnter, ...rest }) => ({
      ...rest
    }))
  }, [menuItems])

  const handleMenuToggle = () => {
    onMenuToggle(menuItemsFull)
  }

  return (
    <Flex w="100%" {...props}>
      <ContentContainer
        direction="column"
        divider={<StackDivider />}
        justify="center"
        overflow="hidden"
        py={0}
        spacing={0}
        maxW={menuMaxContentWidth}
      >
        <HoverableComponent
          alignItems="center"
          display="flex"
          onHover={onMenuHover}
          cursor="default"
        >
          <HStack align="center" flex={1} p={1} w="full">
            <Flex
              alignItems="center"
              display={{
                base: 'flex',
                lg: 'none'
              }}
              flex={1}
            >
              <GrandIconButton
                aria-label="open-menu"
                bg="none"
                color="var(--chakra-colors-component-TopNavContent-hamburger-color, var(--chakra-colors-primary))"
                icon={toggleIcon}
                onClick={handleMenuToggle}
                shadow="none"
                size="sm"
              />
            </Flex>

            <Flex
              cursor="default"
              flex={2}
              justifyContent={{
                base: 'center',
                lg: 'flex-start'
              }}
            >
              <GrandNextLink aria-label="home" cursor="pointer" href="/">
                {logo}
              </GrandNextLink>
            </Flex>

            <HStack
              align="center"
              display={{
                base: 'flex',
                md: 'flex'
              }}
              flex={1}
              justify="flex-end"
              spacing={4}
            >
              {navbarActions.map(({ component, id, showsOnMobile }) => (
                <Box
                  display={{
                    base: showsOnMobile ? 'initial' : 'none',
                    md: 'initial'
                  }}
                  key={id}
                >
                  {component}
                </Box>
              ))}
            </HStack>
          </HStack>
        </HoverableComponent>

        <HStack
          display={{
            base: 'none',
            lg: 'flex'
          }}
          divider={<StackDivider alignSelf="center" h="50%" />}
          fontWeight="medium"
          justify="flex-start"
          py={0}
          spacing={0}
          textAlign="center"
          w="full"
        >
          <HStack spacing={0}>
            {menuItems.categories.map((item) => (
              <TopNavContentMenuItem
                key={item.id}
                {...item}
                isCurrent={!!pathname?.includes(item.path)}
                text={item.text.toUpperCase()}
                menuIsOpened={!isHidden}
              />
            ))}
          </HStack>

          {!!menuItems.other?.length && (
            <HStack>
              {menuItems.other.map((item) => (
                <TopNavContentMenuItem
                  key={item.id}
                  {...item}
                  isCurrent={pathname === item.path}
                  text={item.text.toUpperCase()}
                  menuIsOpened={!isHidden}
                />
              ))}
            </HStack>
          )}
        </HStack>
      </ContentContainer>
    </Flex>
  )
}

export default TopNavContent
